/**
 * Conversational role intents — maps plain English ("PM", "superintendent")
 * to one or more moss.com job-family departments via panel-data roleTaxonomy.
 */
import { normalizeSearchTokens } from './locationSearch';

export interface RoleTaxonomyEntry {
  id: string;
  label: string;
  departments: readonly string[];
  synonyms: readonly string[];
  defaultTrack?: string;
}

export interface RoleTaxonomyIndex {
  byId: ReadonlyMap<string, RoleTaxonomyEntry>;
  synonymToId: ReadonlyMap<string, string>;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Build lookup maps from panel-data roleTaxonomy entries. */
export function buildRoleTaxonomyIndex(
  entries: readonly RoleTaxonomyEntry[],
): RoleTaxonomyIndex {
  const byId = new Map<string, RoleTaxonomyEntry>();
  const synonymToId = new Map<string, string>();

  for (const entry of entries) {
    byId.set(entry.id, entry);
    byId.set(entry.id.replace(/_/g, ' '), entry);
    for (const syn of entry.synonyms) {
      const key = syn.trim().toLowerCase();
      if (key) synonymToId.set(key, entry.id);
    }
    synonymToId.set(entry.label.trim().toLowerCase(), entry.id);
  }

  return { byId, synonymToId };
}

/** Resolve a role intent id from direct input or synonym. */
export function resolveRoleIntent(
  input: string,
  index: RoleTaxonomyIndex,
): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const lc = trimmed.toLowerCase();
  const entry = index.byId.get(lc) ?? index.byId.get(lc.replace(/\s+/g, '_'));
  if (entry) return entry.id;

  const viaSynonym = index.synonymToId.get(lc);
  if (viaSynonym) return viaSynonym;

  for (const [syn, id] of index.synonymToId) {
    if (syn.includes(' ') && lc.includes(syn)) return id;
  }

  return null;
}

export interface RoleIntentResolveArgs {
  roleIntent?: string;
  department?: string;
  search?: string;
}

/**
 * Best-effort role intent from tool args — explicit roleIntent, then
 * department phrase, then role terms embedded in search.
 */
export function resolveRoleIntentFromArgs(
  args: RoleIntentResolveArgs,
  index: RoleTaxonomyIndex,
): { roleIntent?: string; department?: string; search?: string } {
  let roleIntent: string | undefined;
  let department = args.department?.trim() || undefined;
  let search = args.search?.trim() || undefined;

  if (args.roleIntent?.trim()) {
    roleIntent = resolveRoleIntent(args.roleIntent, index) ?? undefined;
  }

  if (!roleIntent && department) {
    const fromDept = resolveRoleIntent(department, index);
    if (fromDept) {
      roleIntent = fromDept;
      department = undefined;
    }
  }

  if (!roleIntent && search) {
    const split = splitSearchAndRoleIntent(search, index);
    if (split.roleIntent) {
      roleIntent = split.roleIntent;
      search = split.search;
    }
  }

  return { roleIntent, department, search };
}

/**
 * When the voice model puts role type in `search`, split into roleIntent +
 * remaining keywords. Mirrors splitSearchAndLocation for geography.
 */
export function splitSearchAndRoleIntent(
  rawSearch: string,
  index: RoleTaxonomyIndex,
): { roleIntent?: string; search?: string } {
  const trimmed = rawSearch.trim();
  if (!trimmed) return {};

  let remainder = trimmed.toLowerCase();
  let roleIntent: string | undefined;

  const multiWordSynonyms = [...index.synonymToId.keys()]
    .filter((k) => k.includes(' '))
    .sort((a, b) => b.length - a.length);

  for (const phrase of multiWordSynonyms) {
    const re = new RegExp(
      `\\b${escapeRegExp(phrase).replace(/\s+/g, '\\s+')}\\b`,
      'i',
    );
    if (!re.test(remainder)) continue;
    roleIntent = index.synonymToId.get(phrase);
    remainder = remainder.replace(re, ' ');
    break;
  }

  if (!roleIntent) {
    const tokens = normalizeSearchTokens(trimmed);
    for (const token of tokens) {
      const id = index.synonymToId.get(token.toLowerCase());
      if (id) {
        roleIntent = id;
        remainder = remainder.replace(
          new RegExp(`\\b${escapeRegExp(token)}\\b`, 'gi'),
          ' ',
        );
        break;
      }
    }
  }

  const searchRemainder = remainder.replace(/\s+/g, ' ').trim();
  return {
    roleIntent,
    search: searchRemainder.length > 0 ? searchRemainder : undefined,
  };
}
